import { type ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { SegmentationProviderId } from "../types/itinerary/segmentation-provider.enum";

/**
 * Whether an evaluated fare amount should be rendered in the UI.
 *
 * @remarks Zero CHF amounts are suppressed (for example GA-covered Swiss CFF segments).
 */
export const isDisplayableEvaluatedPrice = ( price: number | null | undefined ): price is number => {
    return typeof price === "number" && Number.isFinite( price ) && price > 0;
};

/**
 * Maps evaluated prices to a display value, treating zero and invalid amounts as absent.
 *
 * @param price - Raw evaluated price from pricing or offer evaluation.
 * @returns Positive finite amount, or `null` when nothing should be shown.
 */
export const normalizeEvaluatedPriceForDisplay = ( price: number | null | undefined ): number | null => {
    return isDisplayableEvaluatedPrice( price ) ? price : null;
};

/**
 * Sums evaluated segment prices from {@link ItineraryDTO.pricingEvaluation} using
 * {@link ItineraryDTO.pricingSegments} as the join key.
 *
 * @param itinerary - Itinerary containing optional `pricingSegments` and `pricingEvaluation`.
 * @returns Total CHF amount from evaluated pricing-segment rows.
 */
const sumEvaluatedSegmentPricesChf = ( itinerary: ItineraryDTO ): number => {
    const pricingSegments = itinerary.pricingSegments;
    const evaluationSegments = itinerary.pricingEvaluation?.segments;
    if ( pricingSegments === undefined || evaluationSegments === undefined ) {
        return 0;
    }

    let total = 0;
    for ( const segment of pricingSegments ) {
        const row = evaluationSegments[segment.id];
        const price = row?.price;
        if ( typeof price === "number" && Number.isFinite( price ) ) {
            total += price;
        }
    }

    return total;
};

/**
 * Sums evaluated Swiss CFF segment prices from {@link ItineraryDTO.pricingEvaluation} using
 * {@link ItineraryDTO.pricingSegments} as the join key.
 *
 * @param itinerary - Itinerary containing optional `pricingSegments` and `pricingEvaluation`.
 * @returns Total CHF amount attributed to Swiss CFF rows in the pricing evaluation map.
 */
const sumSwissCffEvaluatedSegmentPricesChf = ( itinerary: ItineraryDTO ): number => {
    const pricingSegments = itinerary.pricingSegments;
    const evaluationSegments = itinerary.pricingEvaluation?.segments;
    if ( pricingSegments === undefined || evaluationSegments === undefined ) {
        return 0;
    }

    let total = 0;
    for ( const segment of pricingSegments ) {
        if ( segment.provider?.id !== SegmentationProviderId.swiss_cff ) {
            continue;
        }

        const row = evaluationSegments[segment.id];
        const price = row?.price;
        if ( typeof price === "number" && Number.isFinite( price ) ) {
            total += price;
        }
    }

    return total;
};

/**
 * Resolves the raw itinerary aggregate price in CHF before GA display adjustments.
 *
 * @remarks Falls back to the sum of evaluated segment prices when both
 * {@link ItineraryDTO.price} and {@link ItineraryDTO.pricingEvaluation} totals are absent.
 *
 * @param itinerary - Itinerary with optional aggregate and segment-level pricing evaluation.
 * @returns Aggregate CHF amount, or `null` when no evaluated amount is available.
 */
export const resolveRawItineraryAggregatePriceChf = ( itinerary: ItineraryDTO ): number | null => {
    const aggregatePrice = itinerary.price ?? itinerary.pricingEvaluation?.price ?? null;
    if ( aggregatePrice !== null && aggregatePrice !== undefined ) {
        return aggregatePrice;
    }

    const segmentTotal = sumEvaluatedSegmentPricesChf( itinerary );

    return segmentTotal > 0 ? segmentTotal : null;
};

/**
 * Resolves the itinerary list/header price to display for the current pricing preferences.
 *
 * @remarks When {@link ItineraryPricingPreferencesDTO.swissReductionCard} is `"ga"`, Swiss CFF
 * contributions are subtracted from the resolved aggregate price so list pricing matches GA semantics
 * without calling the pricing API again. When preferences are not GA, the raw aggregate price is
 * returned unchanged. The aggregate is taken from {@link ItineraryDTO.price} when present, otherwise
 * from {@link ItineraryDTO.pricingEvaluation}, otherwise from the sum of evaluated segment prices.
 *
 * @param itinerary - Itinerary with optional `price` and `pricingEvaluation`.
 * @param pricingPreferences - Active pricing preferences (GA triggers the adjustment path).
 * @returns Display price in CHF, or `null` when the itinerary has no aggregate price.
 */
export const resolveItineraryPriceForDisplay = (
    itinerary: ItineraryDTO,
    pricingPreferences: ItineraryPricingPreferencesDTO,
): number | null => {
    const raw = resolveRawItineraryAggregatePriceChf( itinerary );
    if ( raw === null ) {
        return null;
    }

    if ( pricingPreferences.swissReductionCard !== "ga" ) {
        return normalizeEvaluatedPriceForDisplay( raw );
    }

    const swissCharged = sumSwissCffEvaluatedSegmentPricesChf( itinerary );

    return normalizeEvaluatedPriceForDisplay( Math.max( 0, raw - swissCharged ) );
};

/**
 * Whether a trailing asterisk should appear next to the itinerary estimation price badge.
 *
 * @remarks Under GA, the badge shows an adjusted total; hiding the footnote avoids implying the
 * Swiss slice is still a provisional quote when it is treated as included in the subscription.
 * Non-final but complete evaluations (for example indicative CFF fares) do not trigger the footnote.
 *
 * @param itinerary - Itinerary with optional `pricingEvaluation`.
 * @param pricingPreferences - Active pricing preferences.
 * @returns `true` when the aggregate evaluation is incomplete and GA is not active.
 */
export const resolvePricingEvaluationShowIncompleteFootnote = (
    itinerary: ItineraryDTO,
    pricingPreferences: ItineraryPricingPreferencesDTO,
): boolean => {
    if ( pricingPreferences.swissReductionCard === "ga" ) {
        return false;
    }

    const pricingEvaluation = itinerary.pricingEvaluation;
    if ( pricingEvaluation === undefined ) {
        return false;
    }

    return pricingEvaluation.isComplete === false;
};

/**
 * Whether the itinerary list price should use the indicative "from" prefix.
 *
 * @param itinerary - Itinerary with optional `pricingEvaluation`.
 * @returns `true` when the aggregate evaluation exists and is not final.
 */
export const resolvePricingEvaluationShowFromPrefix = ( itinerary: ItineraryDTO ): boolean => {
    const pricingEvaluation = itinerary.pricingEvaluation;
    if ( pricingEvaluation === undefined ) {
        return false;
    }

    return pricingEvaluation.isFinal === false;
};
